"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import Link from "next/link";
import type { UserProfile } from "@/lib/api/user-profile";
import type { CUserAccountsContent } from "@/lib/api/cms-catalog";
import type { FrontendUserSession } from "@/lib/api/session";
import { Icon, type IconName } from "@/lib/icons/Icon";
import { FormStatus } from "@/components/ui/Form";
import { useT } from "@/lib/i18n/context";
import { cUserAccountsTranslations } from "./CUserAccounts.i18n";
import { ProfileTabPersonal } from "./ProfileTabPersonal";
import { ProfileTabPassword } from "./ProfileTabPassword";
import { ProfileTabAccount } from "./ProfileTabAccount";
import { ProfileTabUserList } from "./ProfileTabUserList";
import { ProfileTabSecurity } from "./ProfileTabSecurity";
import { AvatarUploadModal } from "./AvatarUploadModal";
import { PluginSidebarBox } from "./PluginSidebarBox";
import { ExtensionActionView } from "./ExtensionActionView";
import {
  ProfileSidebarNavSkeleton,
  PluginSidebarBoxSkeleton,
  ProfileMainSkeleton,
} from "./ProfileTabsSkeleton";
import {
  fetchUserAccountExtensions,
  type UserAccountExtension,
} from "@/lib/api/user-account-extensions";
import { fetchMfaStatus, MFA_CHANGED_EVENT, type MfaOption, type MfaStatus } from "@/lib/api/mfa";
import styles from "./CUserAccounts.module.css";

/**
 * Wyciąga action_id z URL hash jeśli zawiera fragment z prefix'em `extension-`.
 *
 * Format URL: `/{locale}/{...catalog_path}#extension-{action_id}`
 * Przykład: `/pl/konto#extension-received-messages` -> `"received-messages"`
 *
 * **Dlaczego hash, nie pathname extras:** zmiana hash NIE triggeruje Next.js
 * Server Component re-render (page.tsx pozostaje stabilny). Tylko ProfileTabs
 * (Client Component) reaguje na `hashchange` event i przełącza main area —
 * SPA-like UX bez pełnego reload'u strony przy klik w plugin link.
 *
 * Returns `null` gdy hash nie ma `extension-` prefix'em ani comma-separated
 * listy zawierającej `extension-X`.
 */
function getExtensionActionFromHash(hash: string): string | null {
  if (!hash) return null;
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  if (stripped === "") return null;
  /* Hash może zawierać comma-separated values (ILINK pattern dla anchor). */
  const parts = stripped.split(",");
  for (const piece of parts) {
    if (piece.startsWith("extension-")) {
      return piece.slice("extension-".length);
    }
  }
  return null;
}

/**
 * ProfileTabs — full-page panel zalogowanego user'a z collapsible sidebar.
 *
 * **Layout:**
 *   ```
 *   ┌──────────────┬─────────────────────────────────────────────┐
 *   │ Sidebar      │ Main content area                           │
 *   │ ┌──────────┐ │ (active tab content z paddingami)           │
 *   │ │ [Avatar] │ │                                             │
 *   │ │ Jan K.   │ │                                             │
 *   │ │ jan@ex   │ │                                             │
 *   │ │ [Wyloguj]│ │                                             │
 *   │ └──────────┘ │                                             │
 *   │              │                                             │
 *   │ ▸ Twoje dane │                                             │
 *   │   Hasło      │                                             │
 *   │   Konto      │                                             │
 *   │              │                                             │
 *   │ [←] collapse │                                             │
 *   └──────────────┴─────────────────────────────────────────────┘
 *   ```
 *
 * **Collapsed state:** sidebar zwija się do ~64px (tylko ikony). Persist
 * w localStorage `cms_profile_sidebar_collapsed`.
 *
 * **Nav items — extension point:** aktualnie 3 hardcoded items (Twoje dane,
 * Hasło, Konto). W kolejnych iteracjach inne komponenty systemowe (c_shop
 * "Zamówienia", c_blog "Komentarze", itd.) będą rejestrować dodatkowe items
 * przez backend endpoint (np. `IIAPI_CONTROLLER::userAccountExtensions` lub
 * sekcja w `getApiContent`). Struktura `MenuItem[]` jest extensible — wystarczy
 * dorzucić items do `coreMenuItems` array.
 *
 * **Data flow:**
 *   1. Mount -> GET `/api/iiapi/user` -> fetch profile + fields_config
 *   2. Tab change -> renderuj odpowiedni ProfileTab* z profile + onSaved callback
 *   3. onSaved -> re-fetch profile (świeże dane po update)
 *
 * **Out-of-scope iter 2a:** Company/Billing tabs (2b), change email (2c),
 * delete account (2d), avatar upload (2e), third-party menu items (2f+).
 */
export function ProfileTabs({
  content,
  userSession,
  onLogout,
  logoutBusy,
  logoutError,
}: ProfileTabsProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  /* Lista pluginów rozszerzających shell c_user_accounts (z REST endpoint
     `/cms/c_user_accounts/extensions`). Każdy plugin = osobny item w sidebar
     nav + dedykowany panel z bloków w main area. Polling co `pollingIntervalMs`
     re-fetchuje całość — aktualizuje `sidebar_badge` w sync z blokami. */
  const [extensions, setExtensions] = useState<UserAccountExtension[]>([]);
  /* MFA status resolved przez fetch (`/api/iiapi/user/mfa/status`). Content
     SSR `content.mfa_option` może być 'disabled' nawet gdy user ma grupę
     wymagającą MFA - `getApiContent` w REST context bez pełnej sesji Bearer
     nie widzi `IUSER::isLogged()`. Fetch po mount daje świeży stan per-user
     (endpoint ma `requiresUser: true` -> zawsze zna sesję z Bearer'a).
     Initial value = { option: hint z content, enabled: false } (bez flash
     ukrytej karty gdy SSR jednak zna user'a). Trzymamy cały MfaStatus (nie
     tylko `option`) żeby wykryć `mfaRequiredPending` (required + !enabled)
     i zablokować inne taby. */
  const [mfaStatus, setMfaStatus] = useState<MfaStatus>({
    available: content.mfa_option !== "disabled",
    option: content.mfa_option,
    enabled: false,
  });
  const t = useT(cUserAccountsTranslations);

  const refreshMfa = useCallback(async () => {
    const s = await fetchMfaStatus();
    setMfaStatus(s);
  }, []);

  useEffect(() => {
    void refreshMfa();
  }, [refreshMfa]);

  //Nasłuch custom event z ProfileTabSecurity po enable/disable - odświeżamy
  //stan MFA żeby sidebar/dispatch reagował natychmiast (bez refresh strony)
  //i user mógł opuścić `#security` gdy MFA już skonfigurowane.
  useEffect(() => {
    const onMfaChanged = () => void refreshMfa();
    window.addEventListener(MFA_CHANGED_EVENT, onMfaChanged);
    return () => window.removeEventListener(MFA_CHANGED_EVENT, onMfaChanged);
  }, [refreshMfa]);

  //Wymaga konfiguracji MFA - inne taby zablokowane, dispatcher wymusza render
  //`ProfileTabSecurity` niezależnie od `activeTab`. Auto-redirect na `#security`
  //nie jest tu robiony bo hash routing jest dwukierunkowy (user może wpisać
  //adres z ręki) - zamiast tego blokujemy w renderze (ProfileMainContent) i
  //podświetlamy security w sidebarze.
  const mfaRequiredPending = mfaStatus.option === "required" && !mfaStatus.enabled;

  //Force-navigate na security gdy user ma wymagane MFA i próbuje wpisać inny
  //hash (np. `#personal`) lub przyszedł z linku plugin (`#extension-X`).
  //Wykonujemy w useEffect żeby uniknąć navigate podczas render (loop).
  useEffect(() => {
    if (!mfaRequiredPending) return;
    const raw = (window.location.hash || "").replace(/^#/, "");
    if (raw !== "security") {
      window.location.hash = "security";
    }
  }, [mfaRequiredPending]);

  /* Plugin action wykryty z URL hash (`#extension-{action_id}`) — gdy aktywna,
     main area renderuje `<ExtensionActionView>` zamiast core tab content.
     Reaguje na `hashchange` event (zmiana hash NIE wywołuje Server Component
     re-render — page.tsx stabilne, tylko ProfileTabs aktualizuje state). */
  const [hash, setHash] = useState<string>("");
  useEffect(() => {
    /* Initial sync (SSR -> CSR transition). */
    setHash(window.location.hash);
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  const extensionActionId = getExtensionActionFromHash(hash);

  /* Znajdź extension który obsługuje tę akcję (matchuje po `actions[].href`).
     URL ma format `/{shell_path}#extension-{actionId}` — match po `extension-X`
     marker w href (każda akcja ma unique action_id w hash). */
  let activeExtension: UserAccountExtension | null = null;
  let matchedAction: { key: string; action: UserAccountExtension["actions"][string] } | null = null;
  if (extensionActionId) {
    const urlMarker = `extension-${extensionActionId}`;
    for (const ext of extensions) {
      for (const [key, action] of Object.entries(ext.actions)) {
        if (action.href.includes(urlMarker)) {
          activeExtension = ext;
          matchedAction = { key, action };
          break;
        }
      }
      if (activeExtension) break;
    }
  }

  /* Fetch profile on mount. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/iiapi/user", { cache: "no-store" });
        const body = (await res.json()) as { ok?: boolean; profile?: UserProfile; message?: string };
        if (cancelled) return;
        if (!res.ok || !body.profile) {
          setLoadError(body.message ?? t.profile.loadFailed);
          setLoading(false);
          return;
        }
        setProfile(body.profile);
        setLoading(false);

        /* Dev console.log — diagnostyka fields_config (show_*_tab + per-field
           visibility). Pomaga zweryfikować dlaczego sub-tab / pole nie jest
           widoczne (np. backend zwraca `'0'` zamiast `'1'`/`'2'` dla grupy
           usera). Logowane TYLKO w dev mode. */
        if (process.env.NODE_ENV === "development") {
          /* eslint-disable no-console */
          console.groupCollapsed(
            `[CUserAccounts] profile loaded — id=${body.profile.id} login=${body.profile.login}`
          );
          console.log("Full profile:", body.profile);
          console.log("fields_config:", body.profile.fields_config);
          const tabFlags = {
            show_personal_tab: body.profile.fields_config?.show_personal_tab,
            show_company_tab: body.profile.fields_config?.show_company_tab,
            show_billing_tab: body.profile.fields_config?.show_billing_tab,
            show_prices_tab: body.profile.fields_config?.show_prices_tab,
            show_employee_tab: body.profile.fields_config?.show_employee_tab,
            show_commercial_information_tab: body.profile.fields_config?.show_commercial_information_tab,
            show_shipping_tab: body.profile.fields_config?.show_shipping_tab,
            show_contacts_tab: body.profile.fields_config?.show_contacts_tab,
          };
          console.table(tabFlags);
          console.log("personal fields:", body.profile.fields_config?.personal);
          console.log("company fields:", body.profile.fields_config?.company);
          console.log("billing fields:", body.profile.fields_config?.billing);
          /* Warning dla pól które są widoczne w fields_config (`'1'`/`'2'`)
             ale brakuje ich w UI definition (PERSONAL_FIELDS / COMPANY_FIELDS /
             BILLING_FIELDS w ProfileTabPersonal). Pozwala wykryć "missing UI"
             cases gdy admin doda nowe pole do grupy ale nie ma jeszcze
             renderera. Lista EXPLICIT_EXCLUDED w ProfileTabPersonal pomija
             expected exclusions (id_department/id_workplace/education/login). */
          warnUnhandledFields(body.profile.fields_config);
          console.groupEnd();
          /* eslint-enable no-console */
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* localStorage persist collapse state. */
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem("cms_profile_sidebar_collapsed");
      if (saved === "1") setCollapsed(true);
    } catch { /* SSR / no storage / quota */ }
  }, []);

  /* Fetch user-account extensions + polling co N sekund (default 60s).
     Backend endpoint zwraca atomicznie {blocks + sidebar_badge + actions}
     dla każdego pluginu — polling re-fetchuje wszystko żeby sidebar_badge
     i bloki zostały w sync. Fail-soft: console.warn + zachowaj poprzedni
     state przy network error (nie crashe panelu). */
  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    async function loadExtensions() {
      try {
        const { extensions: rows, pollingIntervalSeconds } = await fetchUserAccountExtensions();
        if (cancelled) return;
        setExtensions(rows);

        /* Lazy-init interval'a po pierwszym pomyślnym fetch'u (wartość polling
           interval'a przychodzi z backendu — może być różna per environment). */
        if (intervalId === null && pollingIntervalSeconds > 0) {
          intervalId = setInterval(() => {
            void loadExtensions();
          }, pollingIntervalSeconds * 1000);
        }
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[CUserAccounts] Fetch extensions failed:", e);
        }
      }
    }

    void loadExtensions();

    return () => {
      cancelled = true;
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, []);

  function toggleCollapse() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem("cms_profile_sidebar_collapsed", next ? "1" : "0");
      } catch { /* ignore */ }
      return next;
    });
  }

  async function refreshProfile() {
    try {
      const res = await fetch("/api/iiapi/user", { cache: "no-store" });
      const body = (await res.json()) as { ok?: boolean; profile?: UserProfile };
      if (res.ok && body.profile) setProfile(body.profile);
    } catch { /* silent — user już widział success */ }
  }

  return (
    <div className={collapsed ? styles.profileLayoutCollapsed : styles.profileLayout}>
      {/* Sidebar column — wrapper grid item zawierający 2 strefy:
            1. <aside> z profile box (avatar/name/logout/nav) — z border + shadow
            2. <PluginSidebarBox> ×N — osobne boksy pod profile box, każdy z
               własnym border/shadow (nie wewnątrz aside).
          Sticky pozycja przeniesiona z aside na wrapper żeby plugin boksy
          również scrollowały razem z aside. */}
      <div className={styles.profileSidebarColumn}>
        <aside className={styles.profileSidebar}>
          <button
            type="button"
            className={styles.profileSidebarCollapseToggle}
            onClick={toggleCollapse}
            title={collapsed ? t.profile.expandMenu : t.profile.collapseMenu}
            aria-label={collapsed ? t.profile.expandMenu : t.profile.collapseMenu}
          >
            <Icon name={collapsed ? "caret-right" : "caret-left"} />
          </button>

          <ProfileSidebarHeader
            profile={profile}
            userSession={userSession}
            lang={content.lang}
            collapsed={collapsed}
            onLogout={onLogout}
            logoutBusy={logoutBusy}
            onAvatarChanged={refreshProfile}
          />

          {logoutError && !collapsed && (
            <div className={styles.profileSidebarError}>
              <FormStatus variant="error">{logoutError}</FormStatus>
            </div>
          )}

          {profile && !loading && !loadError && (
            <ProfileSidebarNav
              collapsed={collapsed}
              profile={profile}
              enableUserList={content.config.enable_user_list === true}
              mfaOption={mfaStatus.option}
              mfaRequiredPending={mfaRequiredPending}
            />
          )}

          {/* Loading state — skeleton dla nav items (header z avatar/name/email
              używa userSession fallback więc renderuje się real). */}
          {loading && !loadError && (
            <ProfileSidebarNavSkeleton collapsed={collapsed} />
          )}
        </aside>

        {/* Plugin boksy — POZA aside, każdy osobno z własnym borderem/shadow.
            Polling co N sekund (z ProfileTabs effect) odświeża sidebar_badge. */}
        {profile && !loading && !loadError && extensions.map((ext) => (
          <PluginSidebarBox
            key={ext.symbol}
            extension={ext}
            collapsed={collapsed}
            disabled={mfaRequiredPending}
          />
        ))}

        {/* Plugin box skeleton w trakcie ładowania extensions (1 placeholder —
            real liczba pluginów nieznana zanim fetch się skończy). */}
        {loading && !loadError && (
          <PluginSidebarBoxSkeleton collapsed={collapsed} />
        )}
      </div>

      <main className={styles.profileMain}>
        {loading && <ProfileMainSkeleton />}

        {loadError && !loading && (
          <FormStatus variant="error">{loadError}</FormStatus>
        )}

        {profile && !loading && !loadError && (
          activeExtension && extensionActionId && !mfaRequiredPending ? (
            /* URL ma `,extension-{action}` extras -> renderuj plugin view zamiast
               core tab. Sidebar nav (Twoje dane / Hasło / Konto) zostaje aktywny —
               kolejny klik w core item zmienia hash i shell wraca do core tabu
               (URL bez extras). Blokujemy plugin view gdy user ma pending MFA
               (must-configure) - musi najpierw skonfigurować. */
            <ExtensionActionView
              extension={activeExtension}
              actionId={extensionActionId}
              matchedAction={matchedAction}
            />
          ) : (
            <ProfileMainContent
              profile={profile}
              content={content}
              userSession={userSession}
              extensions={extensions}
              mfaOption={mfaStatus.option}
              mfaRequiredPending={mfaRequiredPending}
              onProfileUpdated={refreshProfile}
            />
          )
        )}
      </main>
    </div>
  );
}

export type ProfileTabsProps = {
  content: CUserAccountsContent;
  userSession: FrontendUserSession | null;
  onLogout: () => void;
  logoutBusy: boolean;
  logoutError: string | null;
};

/* ─── Sidebar header (avatar + welcome + logout) ─────────────────────── */

function ProfileSidebarHeader({
  profile,
  userSession,
  lang,
  collapsed,
  onLogout,
  logoutBusy,
  onAvatarChanged,
}: {
  profile: UserProfile | null;
  userSession: FrontendUserSession | null;
  lang: CUserAccountsContent["lang"];
  collapsed: boolean;
  onLogout: () => void;
  logoutBusy: boolean;
  onAvatarChanged: () => void;
}) {
  const t = useT(cUserAccountsTranslations);
  const sessionUser = userSession?.user;
  const fullName = profile?.fullname
    || [profile?.name, profile?.surname].filter(Boolean).join(" ").trim()
    || sessionUser?.login
    || "";
  const email = profile?.email ?? sessionUser?.email ?? null;

  const initials = getInitials(fullName || sessionUser?.login || "?");
  /* Sidebar wyświetla `sq_` miniaturę (120×120 center crop) — Imperial standard
     dla małych avatarów. Backend zwraca full URL z domain prefix. */
  const avatarUrl = profile?.avatar_sq_url ?? null;

  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className={styles.profileSidebarHeader}>
      <button
        type="button"
        className={styles.profileAvatarButton}
        onClick={() => setModalOpen(true)}
        title="Zmień avatar"
        aria-label="Zmień avatar"
      >
        <div className={styles.profileAvatar}>
          {avatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={avatarUrl} alt="" className={styles.profileAvatarImg} />
          ) : (
            <span className={styles.profileAvatarInitials}>{initials}</span>
          )}
          <span className={styles.profileAvatarOverlay} aria-hidden="true">
            <Icon name="pencil-simple" />
          </span>
        </div>
      </button>

      {modalOpen && (
        <AvatarUploadModal
          currentAvatarUrl={avatarUrl}
          /* Original (full size) URL — używany do "Przytnij ponownie" funkcji
             w modalu (re-crop bez ponownego uploadu). Pochodzi z `avatar_url`
             w IAPI_user response (separate od `avatar_sq_url` używanego do
             podglądu w sidebarze). */
          currentOriginalUrl={profile?.avatar_url ?? null}
          onClose={() => setModalOpen(false)}
          onSaved={() => onAvatarChanged()}
          onDeleted={() => onAvatarChanged()}
        />
      )}

      {!collapsed && (
        <>
          <div className={styles.profileSidebarHeaderInfo}>
            {fullName && (
              <p className={styles.profileSidebarHeaderName}>{fullName}</p>
            )}
            {email && (
              <p className={styles.profileSidebarHeaderEmail}>{email}</p>
            )}
          </div>

          <button
            type="button"
            className={styles.profileSidebarLogoutBtn}
            onClick={onLogout}
            disabled={logoutBusy}
          >
            {logoutBusy ? "…" : lang.actions?.logout ?? t.actions.logout}
          </button>
        </>
      )}

      {collapsed && (
        <button
          type="button"
          className={styles.profileSidebarLogoutIcon}
          onClick={onLogout}
          disabled={logoutBusy}
          title={lang.actions?.logout ?? t.actions.logout}
          aria-label={lang.actions?.logout ?? t.actions.logout}
        >
          <Icon name="arrow-square-out" />
        </button>
      )}
    </div>
  );
}

/* ─── Sidebar nav items (3 core tabs — Personal / Password / Account) ─── */

type TabKey = "personal" | "password" | "account" | "users" | "security";

type MenuItem = {
  key: TabKey;
  label: string;
  icon: IconName;
  visible: boolean;
};

function isCoreHash(h: string): h is TabKey {
  return h === "personal" || h === "password" || h === "account" || h === "users" || h === "security";
}

function ProfileSidebarNav({
  collapsed,
  profile,
  enableUserList,
  mfaOption,
  mfaRequiredPending,
}: {
  collapsed: boolean;
  profile: UserProfile;
  enableUserList: boolean;
  /** Polityka MFA agregowana z grup usera. Karta "Bezpieczeństwo" widoczna
      gdy `!== "disabled"`. */
  mfaOption: "disabled" | "optional" | "required";
  /** `true` gdy user musi skonfigurować MFA (required + !enabled). Inne taby
      są wtedy nieklikalne (visual disabled + preventDefault na kliku), user
      dostaje jasny sygnał że musi przejść przez security najpierw. */
  mfaRequiredPending: boolean;
}) {
  const t = useT(cUserAccountsTranslations);
  /* Hash routing dla wszystkich widoków w panelu klienta:
       core tabs: `#personal`, `#password`, `#account`
       plugin actions: `#extension-{action_id}`
     Hash zmiana NIE wywołuje Next.js Server Component re-render (page.tsx
     stabilne) — wszystko aktualizuje się client-side w ProfileTabs.
     `activeTab=null` gdy hash to extension action -> żaden core tab nie ma
     active class, plugin link w sidebar boksie pokazuje active state. */
  const [activeTab, setActiveTab] = useState<TabKey | null>(null);

  useEffect(() => {
    const resolveActive = () => {
      const raw = (window.location.hash || "").replace(/^#/, "");
      /* Hash z prefix'em `extension-` -> plugin view aktywny -> core tab inactive. */
      if (raw.startsWith("extension-") || raw.split(",").some((p) => p.startsWith("extension-"))) {
        setActiveTab(null);
        return;
      }
      if (isCoreHash(raw)) {
        setActiveTab(raw);
        return;
      }
      /* Domyślnie: personal (lub password gdy personal niewidoczne). */
      setActiveTab(profile.fields_config?.show_personal_tab ? "personal" : "password");
    };
    resolveActive();
    window.addEventListener("hashchange", resolveActive);
    return () => window.removeEventListener("hashchange", resolveActive);
  }, [profile.fields_config?.show_personal_tab]);

  const coreMenuItems: MenuItem[] = [
    {
      key: "personal",
      label: t.profile.tabs.personal,
      icon: "user",
      visible: profile.fields_config?.show_personal_tab === true,
    },
    {
      key: "password",
      label: t.profile.tabs.password,
      icon: "lock",
      visible: true,
    },
    {
      key: "account",
      label: t.profile.tabs.account,
      icon: "gear",
      visible: true,
    },
    {
      key: "users",
      label: t.profile.tabs.users,
      icon: "list-bullets",
      visible: enableUserList,
    },
    {
      key: "security",
      label: t.profile.tabs.security,
      icon: "lock",
      visible: mfaOption !== "disabled",
    },
  ];

  const visibleItems = coreMenuItems.filter((it) => it.visible);

  return (
    <nav className={styles.profileSidebarNav} role="tablist">
      {visibleItems.map((item) => {
        //Blokada innych taby gdy MFA wymagane (required + !enabled): tylko
        //`security` klikalne. Wizualnie disabled + `aria-disabled` + brak href
        //(preventDefault na kliku nie wystarczy - user może otworzyć w nowej
        //karcie; bez href browser NIE navigate'uje).
        const isDisabled = mfaRequiredPending && item.key !== "security";
        return (
          <a
            key={item.key}
            /* `<a href="#key">` zamiast Next.js `<Link>` — hash-only nav,
               żadnego Server Component re-render. Klik trigger'uje
               `hashchange` event który ProfileTabs słucha (i ProfileSidebarNav
               via własny useEffect listener poniżej). */
            href={isDisabled ? undefined : `#${item.key}`}
            role="tab"
            aria-selected={activeTab === item.key}
            aria-disabled={isDisabled || undefined}
            className={
              isDisabled
                ? styles.profileSidebarNavItemDisabled
                : activeTab === item.key
                  ? styles.profileSidebarNavItemActive
                  : styles.profileSidebarNavItem
            }
            title={
              isDisabled
                ? t.mfaGuard.title
                : collapsed
                  ? item.label
                  : undefined
            }
            onClick={(e) => {
              if (isDisabled) e.preventDefault();
            }}
          >
            <Icon name={item.icon} className={styles.profileSidebarNavIcon} />
            {!collapsed && <span>{item.label}</span>}
          </a>
        );
      })}
    </nav>
  );
}

/* ─── Main content area (active tab dispatch) ────────────────────────── */

function ProfileMainContent({
  profile,
  content,
  userSession,
  extensions,
  mfaOption,
  mfaRequiredPending,
  onProfileUpdated,
}: {
  profile: UserProfile;
  content: CUserAccountsContent;
  userSession: FrontendUserSession | null;
  extensions: UserAccountExtension[];
  /** Świeży stan polityki MFA z fetch mfaStatus (patrz ProfileTabs). */
  mfaOption: MfaOption;
  /** `true` gdy user musi skonfigurować MFA - dispatcher forsuje render
      `<ProfileTabSecurity>` niezależnie od `activeTab` (nawet gdy hash to
      `#personal` czy `#account`). Zabezpiecza przed race parent redirect vs
      lokalny hash state. */
  mfaRequiredPending: boolean;
  onProfileUpdated: () => void;
}) {
  /* Sync z hash routing (spójne z ProfileSidebarNav). */
  const [activeTab, setActiveTab] = useState<TabKey>(
    profile.fields_config?.show_personal_tab ? "personal" : "password",
  );

  useEffect(() => {
    const resolveActive = () => {
      const raw = (window.location.hash || "").replace(/^#/, "");
      if (isCoreHash(raw)) {
        setActiveTab(raw);
      } else {
        /* Niecore hash (extension lub puste) -> default tab. ProfileTabs
           routing decyduje czy w ogóle renderować `<ProfileMainContent>`
           czy `<ExtensionActionView>` — tutaj tylko trzymamy default
           dla powrotu z plugin view. */
        setActiveTab(profile.fields_config?.show_personal_tab ? "personal" : "password");
      }
    };
    resolveActive();
    window.addEventListener("hashchange", resolveActive);
    return () => window.removeEventListener("hashchange", resolveActive);
  }, [profile.fields_config?.show_personal_tab]);

  //Wymuszony render security gdy pending - zabezpieczenie na wypadek race'a
  //parent redirect (`window.location.hash = 'security'`) vs lokalny state
  //`activeTab`. User widzi setup panel niezależnie od tego jaki hash się
  //jeszcze nie zsynchronizował.
  if (mfaRequiredPending) {
    return <ProfileTabSecurity mfaOption={mfaOption} />;
  }

  let tabContent: ReactNode = null;
  if (activeTab === "personal" && profile.fields_config?.show_personal_tab) {
    tabContent = <ProfileTabPersonal profile={profile} onSaved={onProfileUpdated} />;
  } else if (activeTab === "password") {
    tabContent = <ProfileTabPassword />;
  } else if (activeTab === "account") {
    tabContent = (
      <ProfileTabAccount
        content={content}
        profile={profile}
        userSession={userSession}
        onProfileRefresh={onProfileUpdated}
      />
    );
  } else if (activeTab === "users" && content.config.enable_user_list) {
    tabContent = <ProfileTabUserList content={content} extensions={extensions} />;
  } else if (activeTab === "security" && mfaOption !== "disabled") {
    tabContent = <ProfileTabSecurity mfaOption={mfaOption} />;
  }

  return <>{tabContent}</>;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

/**
 * Dev helper — log warning dla pól które są w fields_config jako widoczne
 * (`'1'` lub `'2'`) ALE brakuje ich w UI definition (PERSONAL_FIELDS /
 * COMPANY_FIELDS / BILLING_FIELDS w ProfileTabPersonal). Pozwala wykryć
 * "missing UI" cases gdy admin doda nowe pole do konfiguracji grupy ale
 * frontend nie ma jeszcze renderera.
 *
 * **Skip list:** pola admin-managed lub spoza whitelist `IAPI_userUpdate`
 * (id_department, id_workplace, education, login + parent aliases jak
 * `about_me` które są renderowane jako `about_me_pl/en/de` per locale).
 */
function warnUnhandledFields(fc: UserProfile["fields_config"] | undefined): void {
  if (!fc) return;
  /* Lista pól które UI obsługuje (per sekcja). Musi być spójna z
     PERSONAL_FIELDS / COMPANY_FIELDS / BILLING_FIELDS w ProfileTabPersonal.
     Plus `configKey` aliases (np. `about_me` jest aliasem dla `about_me_pl`). */
  const handledPersonal = new Set([
    "name", "surname", "sex", "birth_date", "pesel", "nr_id_card",
    "telephone", "www", "country", "city", "street", "nr", "post_code",
    "province", "state", "nip", "bank_account_number", "about_me",
    "facebook_account_url", "twitter_account_url", "linkedin_account_url",
    "youtube_account_url", "pinterest_account_url",
  ]);
  const handledCompany = new Set([
    "company_name", "company_nip", "company_krs", "regon",
    "company_country", "company_city", "company_street", "company_nr",
    "company_post_code", "company_province", "company_state",
    "company_telephone", "company_fax", "company_www",
    "company_contact_person_name", "company_contact_person_surname",
    "company_trade", "company_bank_account_number",
  ]);
  const handledBilling = new Set([
    "billing_name", "billing_nip", "billing_country", "billing_city",
    "billing_street", "billing_nr", "billing_post_code",
  ]);
  /* Pola explicit excluded — admin-managed / poza whitelist `IAPI_userUpdate`.
     NIE generujemy warning bo to expected exclusions.
     - id_department / id_workplace : admin-managed (form widoczny tylko w users_manager admin)
     - login : read-only display w panelu admina, nie edytowalne
     Uwaga: 'education' usunięte z systemu (commit 7fc82e54) — klucz może
     pozostać w starych wpisach required_fields_config, helper PHP
     REMOVED_LEGACY_FIELDS go ignoruje. */
  const excludedPersonal = new Set([
    "id_department", "id_workplace", "login",
  ]);
  /* Sekcja `company` — explicit excluded pola CRM-only. UI w m_crm_contractors,
     nie w c_user_accounts. Helper PHP CRM_ONLY_COMPANY_FIELDS pomija je w
     walidacji konta użytkownika; IAPI_userUpdate whitelist też ich nie zawiera. */
  const excludedCompany = new Set([
    "company_language", "company_related_buyer", "jst", "gv", "idwew",
  ]);

  const checkSection = (
    sectionName: string,
    sectionConfig: Record<string, unknown> | undefined | null,
    handled: Set<string>,
    excluded: Set<string>,
  ) => {
    if (!sectionConfig) return;
    const missing: string[] = [];
    for (const [field, value] of Object.entries(sectionConfig)) {
      const isVisible = value === "1" || value === "2" || value === 1 || value === 2;
      if (!isVisible) continue;
      if (excluded.has(field)) continue;
      if (handled.has(field)) continue;
      missing.push(field);
    }
    if (missing.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `[CUserAccounts] Brak UI definition dla pól w sekcji "${sectionName}":`,
        missing,
        "-> dodaj do PERSONAL_FIELDS/COMPANY_FIELDS/BILLING_FIELDS w ProfileTabPersonal.tsx",
      );
    }
  };

  checkSection("personal", fc.personal as Record<string, unknown> | undefined, handledPersonal, excludedPersonal);
  checkSection("company", fc.company as Record<string, unknown> | undefined, handledCompany, excludedCompany);
  checkSection("billing", fc.billing as Record<string, unknown> | undefined, handledBilling, new Set());
}

/** "Jan Kowalski" -> "JK"; "jankowal" -> "J"; bezpieczne fallback "?". */
function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? "?").toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
}
